import React, { useCallback } from 'react';
import { DraggableModal } from '@ui/shared/components/Common/DraggableModal';
import { ReactChat } from '@shared/components/proctoring/ReactChat';
import useAppStore from '../../stores/app';
import config from '../../config';

/**
 * RoomChatModal Component
 * 
 * Draggable modal wrapper for the ReactChat component.
 * Provides a chat interface for the proctor room using the LiveKit room connection.
 * Opens when triggered from the VTC interface.
 * Tracks unread messages when the modal is closed.
 */
export const RoomChatModal = () => {
    const isOpen = useAppStore((state) => state.isRoomChatModalOpen);
    const setRoomChatModalOpen = useAppStore((state) => state.setRoomChatModalOpen);
    const roomSid = useAppStore((state) => state.roomSid);
    const user = useAppStore((state) => state.user);
    const mediaClient = useAppStore((state) => state.mediaClient);
    const incrementUnreadRoomChatCount = useAppStore((state) => state.incrementUnreadRoomChatCount);

    const handleClose = () => {
        setRoomChatModalOpen(false);
    };

    /**
     * Handle new messages - increment unread count if modal is closed
     */
    const handleNewMessage = useCallback((message) => {
        // Only increment unread count if the modal is closed and the message is not from the current user
        if (!isOpen && message.identity !== user?.sid) {
            incrementUnreadRoomChatCount();
        }
    }, [isOpen, user?.sid, incrementUnreadRoomChatCount]);

    // Don't render if we don't have the required data
    if (!roomSid || !user || !mediaClient) {
        return null;
    }

    return (
        <DraggableModal
            isOpen={isOpen}
            onClose={handleClose}
            title="Room Chat"
            initialPosition={{ x: 100, y: 100 }}
            initialSize={{ width: 500, height: 600 }}
            minSize={{ width: 350, height: 400 }}
            maxSize={{ width: 800, height: 900 }}
            resizable={true}
            minimizable={true}
            id="vtc-room-chat-modal"
        >
            <ReactChat
                roomId={roomSid}
                identity={user.sid}
                token={user.token}
                referenceId={roomSid}
                apiUrl={config.proctoring?.api?.baseUrl}
                mediaAgent={mediaClient}
                displayName={`${user.firstName} ${user.lastName}`}
                allowNewMessage={true}
                onNewMessage={handleNewMessage}
            />
        </DraggableModal>
    );
};

export default RoomChatModal;
